import React, { useMemo, useState } from 'react'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts'
import { useCustomerAnalyticsData } from '../../hooks/useCustomerAnalyticsData'

const COLORS = { active: '#1D9E75', inactive: '#B4B2A9', bar: '#A0826D', top: '#7F77DD' }
const avatarColors = ['#A0826D', '#7F77DD', '#1D9E75', '#D85A30', '#D4537E']
const CARD = "bg-white rounded-2xl border border-gray-200 shadow-[0_1px_4px_rgba(0,0,0,0.05)]"

function Pagination({ page, totalItems, pageSize, onPageChange }) {
  const totalPages = Math.ceil(totalItems / pageSize)
  if (totalPages <= 1) return null

  return (
    <div className="flex items-center justify-between px-4 py-2">
      <span className="text-[11px] text-gray-400">
        {Math.min((page - 1) * pageSize + 1, totalItems)}–{Math.min(page * pageSize, totalItems)} of {totalItems} customers
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page === 1}
          className="px-2.5 py-1 rounded-lg border text-[11px]"
          style={{
            borderColor: '#e5e7eb',
            color: page === 1 ? '#e5e7eb' : '#6b7280',
            cursor: page === 1 ? 'not-allowed' : 'pointer',
          }}>
          ← Prev
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
          .reduce((acc, n, idx, arr) => {
            if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…")
            acc.push(n)
            return acc
          }, [])
          .map((n, idx) =>
            n === "…" ? (
              <span key={`e-${idx}`} className="text-[11px] text-gray-400 px-0.5">…</span>
            ) : (
              <button key={n} onClick={() => onPageChange(n)}
                className="w-7 h-7 rounded-lg border text-[11px] font-semibold"
                style={{
                  borderColor: n === page ? '#A0826D' : '#e5e7eb',
                  background: n === page ? '#A0826D' : '#fff',
                  color: n === page ? '#fff' : '#6b7280',
                  cursor: 'pointer',
                }}>
                {n}
              </button>
            )
          )
        }
        <button
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
          className="px-2.5 py-1 rounded-lg border text-[11px]"
          style={{
            borderColor: '#e5e7eb',
            color: page === totalPages ? '#e5e7eb' : '#6b7280',
            cursor: page === totalPages ? 'not-allowed' : 'pointer',
          }}>
          Next →
        </button>
      </div>
    </div>
  )
}

export default function CustomerData() {
  const { customers, orders, loading, error, refetch } = useCustomerAnalyticsData()
  const [page, setPage] = useState(1)
  const PAGE_SIZE = 10

  const {
    stats, dailyNewCustomers, statusData, topCustomers, allCustomers
  } = useMemo(() => {
    if (!customers.length && !orders.length) {
      return {
        stats: { newCustomers7d: 0, activeUsers: 0, activePercentage: 0, returningBuyers: 0, avgOrdersPerCustomer: 0 },
        dailyNewCustomers: [], statusData: [], topCustomers: [], recentCustomers: [], allCustomers: [],
      }
    }

    const now = new Date()
    const sevenDaysAgo = new Date(now - 7 * 24 * 60 * 60 * 1000)
    const fourteenDaysAgo = new Date(now - 14 * 24 * 60 * 60 * 1000)

    const newCustomers7d = customers.filter(c => new Date(c.created_at) >= sevenDaysAgo).length

    const activeUsers = customers.filter(c => c.account_status === 'active').length
    const activePercentage = customers.length > 0 ? (activeUsers / customers.length) * 100 : 0

    const orderCountMap = {}
    const customerSpendMap = {}
    orders.forEach(o => {
      if (o.order_status === 'completed') {
        orderCountMap[o.user_id] = (orderCountMap[o.user_id] || 0) + 1
        customerSpendMap[o.user_id] = (customerSpendMap[o.user_id] || 0) + parseFloat(o.final_amount || 0)
      }
    })

    const returningBuyers = customers.length > 0
      ? (Object.values(orderCountMap).filter(c => c >= 2).length / customers.length) * 100
      : 0

    const totalOrders = Object.values(orderCountMap).reduce((a, b) => a + b, 0)
    const avgOrdersPerCustomer = customers.length > 0 ? totalOrders / customers.length : 0

    const dailyMap = {}
    for (let i = 13; i >= 0; i--) {
      const d = new Date(now)
      d.setDate(d.getDate() - i)
      const key = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      dailyMap[key] = 0
    }
    customers.forEach(c => {
      const date = new Date(c.created_at)
      if (date >= fourteenDaysAgo) {
        const key = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        if (dailyMap[key] !== undefined) dailyMap[key]++
      }
    })

    const inactiveUsers = customers.length - activeUsers

    const top5 = customers
      .map(c => ({
        name: `${c.first_name?.charAt(0) || ''}. ${c.last_name || ''}`,
        spend: parseFloat((customerSpendMap[c.user_id] || 0).toFixed(2)),
      }))
      .sort((a, b) => b.spend - a.spend)
      .slice(0, 5)
      .reverse()

    const mapWithStats = (c) => {
      const customerOrders = orders.filter(o => o.user_id === c.user_id)
      const totalSpent = customerOrders
        .filter(o => o.order_status === 'completed')
        .reduce((sum, o) => sum + parseFloat(o.final_amount || 0), 0)
      return { ...c, orderCount: customerOrders.length, totalSpent }
    }

  const allMapped = customers.map(mapWithStats)

  return {
    stats: { newCustomers7d, activeUsers, activePercentage, returningBuyers, avgOrdersPerCustomer },
    dailyNewCustomers: Object.entries(dailyMap).map(([date, count]) => ({ date, count })),
    statusData: [{ name: 'Active', value: activeUsers }, { name: 'Inactive', value: inactiveUsers }],
    topCustomers: top5,
    allCustomers: allMapped,
  }
  }, [customers, orders])

  const formatCurrency = (amount) =>
    `₱${parseFloat(amount || 0).toLocaleString('en-PH', {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    })}`

  if (loading) return (
    <div className="flex items-center justify-center py-8">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8B5A3C]" />
    </div>
  )

  if (error) return (
    <div className="flex flex-col items-center justify-center py-8 gap-2">
      <p className="text-sm text-red-600">Failed to load customer data</p>
      <p className="text-xs text-gray-400 mb-1">{error}</p>
      <button onClick={refetch} className="px-3 py-1.5 bg-[#A0826D] text-white rounded-lg text-xs font-medium hover:bg-[#8B6F5F]">
        Retry
      </button>
    </div>
  )

  const pagedCustomers = allCustomers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  return (
    <div className="space-y-4">
      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className={`${CARD} p-4`}>
          <p className="text-xs text-gray-400 mb-1">New customers (7d)</p>
          <p className="text-3xl font-bold text-gray-800">{stats.newCustomers7d}</p>
        </div>
        <div className={`${CARD} p-4`}>
          <p className="text-xs text-gray-400 mb-1">Active accounts</p>
          <p className="text-3xl font-bold text-gray-800">{stats.activeUsers.toLocaleString()}</p>
          <p className="text-xs text-gray-400">{stats.activePercentage.toFixed(0)}% of total</p>
        </div>
        <div className={`${CARD} p-4`}>
          <p className="text-xs text-gray-400 mb-1">Returning buyers</p>
          <p className="text-3xl font-bold text-gray-800">{stats.returningBuyers.toFixed(0)}%</p>
          <p className="text-xs text-gray-400">placed 2+ orders</p>
        </div>
        <div className={`${CARD} p-4`}>
          <p className="text-xs text-gray-400 mb-1">Avg orders / customer</p>
          <p className="text-3xl font-bold text-gray-800">{stats.avgOrdersPerCustomer.toFixed(1)}</p>
        </div>
      </div>

      {/* New customers per day */}
      <div className={`${CARD} p-4`}>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
          New customers — last 14 days
        </p>
        <ResponsiveContainer debounce={200} width="100%" height={160}>
          <BarChart data={dailyNewCustomers} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#888780' }} interval={1} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 10, fill: '#888780' }} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '0.5px solid #e5e7eb' }} formatter={(v) => [v, 'New customers']} />
            <Bar dataKey="count" fill={COLORS.bar} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className={`${CARD} p-4`}>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Account status</p>
          <div className="flex gap-3 mb-2">
            <span className="flex items-center gap-1 text-xs text-gray-400 mb-1">
              <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: COLORS.active }} />
              Active
            </span>
            <span className="flex items-center gap-1 text-xs text-gray-400 mb-1">
              <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: COLORS.inactive }} />
              Inactive
            </span>
          </div>
          <ResponsiveContainer debounce={200} width="100%" height={150}>
            <PieChart>
              <Pie data={statusData} cx="50%" cy="50%" innerRadius="52%" outerRadius="78%" dataKey="value" strokeWidth={0}>
                {statusData.map((_, i) => (
                  <Cell key={i} fill={i === 0 ? COLORS.active : COLORS.inactive} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '0.5px solid #e5e7eb' }} formatter={(v, name) => [v.toLocaleString(), name]} />
              <text x="50%" y="44%" textAnchor="middle" dominantBaseline="middle" fill={COLORS.active} fontSize={18} fontWeight={500}>
                {stats.activePercentage.toFixed(0)}%
              </text>
              <text x="50%" y="58%" textAnchor="middle" dominantBaseline="middle" fill="#888780" fontSize={11}>
                active
              </text>
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className={`${CARD} p-4`}>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Top customers by spend</p>
          <ResponsiveContainer debounce={200} width="100%" height={150}>
            <BarChart layout="vertical" data={topCustomers} margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
              <XAxis type="number" tick={{ fontSize: 10, fill: '#888780' }} tickLine={false} axisLine={false} tickFormatter={(v) => `₱${(v / 1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#888780' }} tickLine={false} axisLine={false} width={60} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '0.5px solid #e5e7eb' }} formatter={(v) => [formatCurrency(v), 'Lifetime spend']} />
              <Bar dataKey="spend" fill={COLORS.top} radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className={`${CARD} overflow-hidden`}>
        <p className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-100">
          All customers ({allCustomers.length})
        </p>
        {allCustomers.length === 0 ? (
          <p className="px-4 py-6 text-sm text-center text-gray-400">No customers yet</p>
        ) : (
          pagedCustomers.map((customer, i) => (
            <div key={customer.user_id} className="flex items-center gap-3 px-4 py-3 border-b border-gray-50 last:border-0">
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                style={{ backgroundColor: avatarColors[i % avatarColors.length] }}>
                {customer.first_name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">
                  {customer.first_name} {customer.last_name}
                </p>
                <p className="text-xs text-gray-400">
                  {customer.orderCount} order{customer.orderCount !== 1 ? 's' : ''} · {formatCurrency(customer.totalSpent)} lifetime
                </p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                customer.account_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
              }`}>
                {customer.account_status}
              </span>
            </div>
          ))
        )}
        <Pagination
          page={page}
          totalItems={allCustomers.length}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      </div>
    </div>
  )
}